import { Box, Button, Card, CircularProgress, LinearProgress, Skeleton, Typography } from "@mui/material";
import { BubbleMenu, useEditor } from "@tiptap/react";
import { API, Auth } from "aws-amplify";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AddPaymentModal } from "src/components/dashboard/addPaymenModal";
import { RegenerateDropdown } from "src/components/dashboard/brief/rightSection/aiArticle/regenerateDropdown";
import ImageGenerationModal from "src/components/dashboard/editor/ImageGenerationModal";
import MenuBar from "src/components/dashboard/editor/MenuBar";
import { MagicWandIcon } from "src/icons/magic-wand-icon";
import { getActivePlan } from "src/slices/auth/thunks";

import Head from "next/head";
import {
  selectIsHumanized,
  setIsHumanized,
  setShowHumanizeContent,
} from "src/slices/humanize";
import { endHumanization } from "src/slices/humanize/thunks";
import { applyFactCheckFinding } from "src/slices/articles/thunks";
import { aiWordsUpgradeModalShowed, snackAdded } from "src/slices/system";
import { selectCurrentPlan } from "src/slices/system/selectors";
import { useDispatch, useSelector } from "src/store";
import { copyHtmlToClipboard, insertUnlockCardWithTipTap } from "src/utils/clipboardUtils";
import { Export2Docx } from "src/utils/docs";
import { signedHumanizeFetch } from "src/utils/humanizeStream";
import {
  addMetaToArticle,
  countSelectedWords,
  extractSelectedBlocks,
  getEditorExtensions,
  getMarkdownConverter,
  getWordCount,
  SelectedTextItem,
  setupTurndownService,
  useDualDebounce,
} from "src/utils/editorUtils";
import Editor from "../editor";
import { FactCheckDecorationRange } from "../editor/extensions/FactCheckDecorations";
import { Onboarding } from "../onboarding";
import { OptimizeContentDialog } from "../optimizeContent/optimizeContentDialog";
import { AutoReview, FactCheckFinding } from "./factCheckSidebar";
import Sidebar from "./sidebar";

// Props for the ArticleEditorComponent
interface ArticleEditorComponentProps {
  id: string;
  title?: string;
  description?: string;
  isOptimizedMode?: boolean;
  hideArticle?: boolean;
  fetchContent: (id: string) => Promise<any>;
  saveContent: (content: string, options?: any) => Promise<any>;
  isDownloadContent?: boolean;
  isCopyContent?: boolean;
  additionalProps?: any;
  isEEATMode?: boolean;
}

const buildNormalizedTextMap = (input: string) => {
  let normalized = "";
  const normalizedToRaw: number[] = [];
  let previousWasSpace = true;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    const isSpace = /\s/.test(char);

    if (isSpace) {
      if (!previousWasSpace) {
        normalized += " ";
        normalizedToRaw.push(i);
        previousWasSpace = true;
      }
      continue;
    }

    normalized += char.toLowerCase();
    normalizedToRaw.push(i);
    previousWasSpace = false;
  }

  if (normalized.endsWith(" ")) {
    normalized = normalized.slice(0, -1);
    normalizedToRaw.pop();
  }

  return { normalized, normalizedToRaw };
};

const unwrapDynamoValue = (value: any): any => {
  if (value == null || typeof value !== "object") return value;
  if ("S" in value) return value.S;
  if ("N" in value) return Number(value.N);
  if ("BOOL" in value) return Boolean(value.BOOL);
  if ("NULL" in value) return null;
  if ("M" in value) {
    const out: Record<string, any> = {};
    Object.entries(value.M || {}).forEach(([k, v]) => {
      out[k] = unwrapDynamoValue(v);
    });
    return out;
  }
  if ("L" in value) {
    return (value.L || []).map((item: any) => unwrapDynamoValue(item));
  }
  return value;
};

/** Normalize unicode characters that commonly differ between AI output and
 *  rich-text editors: smart quotes, em/en dashes. All replacements are
 *  1-to-1 character length to preserve offset mapping when applied to both
 *  needle and haystack. */
const normalizeUnicode = (s: string): string =>
  s
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")   // smart single quotes → '
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')   // smart double quotes → "
    .replace(/[\u2013\u2014]/g, "-");               // en-dash / em-dash → -

/** Convert markdown table-like text to plain visible text used by TipTap.
 *  Example: "| A | B |" -> "A B" */
const normalizeMarkdownTableText = (input: string): string => {
  const text = normalizeUnicode(input || "").trim();
  if (!text.includes("|")) return text;

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const normalizedLines = lines
    // Drop markdown separator lines like | --- | :---: |
    .filter((line) => !/^[\|\-\:\s]+$/.test(line))
    .map((line) => {
      const noEdgePipes = line.replace(/^\|/, "").replace(/\|$/, "");
      return noEdgePipes
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean)
        .join(" ");
    })
    .filter(Boolean);

  return normalizedLines.join(" ").replace(/\s+/g, " ").trim();
};

const buildTextSegments = (editor: any) => {
  const segments: Array<{ start: number; end: number; pos: number; text: string; isNewline?: boolean }> = [];
  let cursor = 0;
  let lastBlockEnd = -1;

  editor.state.doc.descendants((node: any, pos: number) => {
    // Insert newline separator between block-level nodes so cross-paragraph
    // text matching behaves correctly (matches markdown newline boundaries).
    if (node.isBlock && !node.isLeaf && segments.length > 0 && pos > lastBlockEnd) {
      segments.push({ start: cursor, end: cursor + 1, pos, text: "\n", isNewline: true });
      cursor += 1;
    }
    if (node.isBlock) {
      lastBlockEnd = pos + node.nodeSize;
    }
    if (!node.isText || !node.text) return;
    const text = String(node.text);
    const start = cursor;
    const end = start + text.length;
    segments.push({ start, end, pos, text });
    cursor = end;
  });
  return { segments, combinedText: segments.map((s) => s.text).join("") };
};

const charIndexToPos = (
  segments: Array<{ start: number; end: number; pos: number; isNewline?: boolean }>,
  index: number,
  bias: "start" | "end" = "start"
) => {
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    // Use exclusive upper bound so boundary indexes (text.end === newline.start)
    // fall through to the next segment. The last segment uses inclusive end
    // to handle the document-end position.
    const atEnd = i === segments.length - 1;
    if (index >= segment.start && (atEnd ? index <= segment.end : index < segment.end)) {
      if (segment.isNewline) {
        // Virtual newline — snap to adjacent real text to avoid landing on a
        // paragraph boundary (which would merge paragraphs on replace).
        // Bias-aware: "start" snaps forward, "end" snaps backward.
        if (bias === "start") {
          const next = segments[i + 1];
          if (next && !next.isNewline) return next.pos;
          const prev = segments[i - 1];
          if (prev && !prev.isNewline) return prev.pos + (prev.end - prev.start);
        } else {
          const prev = segments[i - 1];
          if (prev && !prev.isNewline) return prev.pos + (prev.end - prev.start);
          const next = segments[i + 1];
          if (next && !next.isNewline) return next.pos;
        }
        return null;
      }
      const offset = Math.max(0, Math.min(index - segment.start, segment.end - segment.start));
      return segment.pos + offset;
    }
  }
  return null;
};

/** Check whether two strings are close enough to be considered the same text
 *  after minor whitespace / punctuation drift. */
const isCloseMatch = (a: string, b: string): boolean => {
  const na = a.toLowerCase().replace(/\s+/g, " ").trim();
  const nb = b.toLowerCase().replace(/\s+/g, " ").trim();
  if (na === nb) return true;
  if (na.length > 0 && nb.length > 0) {
    const shorter = Math.min(na.length, nb.length);
    const longer = Math.max(na.length, nb.length);
    // Allow up to 20% length difference and check prefix containment
    if (shorter / longer > 0.8 && na.includes(nb.slice(0, shorter))) return true;
  }
  return false;
};


type PrebuiltSegments = { segments: Array<{ start: number; end: number; pos: number; text: string; isNewline?: boolean }>; combinedText: string };

const getArticleApiBaseUrl = () => {
  if (
    process.env.NEXT_PUBLIC_BRANCH === "dev" ||
    process.env.NODE_ENV === "development"
  ) {
    return "https://py-staging.seowind.io";
  }
  if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
    return "https://py-staging.seowind.io";
  }
  return "https://py.seowind.io";
};

/** Score how well surrounding text matches the locator context.
 *  0 = no context or no match, 1 = partial match, 2 = full match per side. Max 4. */
const scoreContextMatch = (
  combinedText: string,
  matchStart: number,
  matchEnd: number,
  locator?: FactCheckFinding["locator"]
): number => {
  if (!locator) return 0;
  let score = 0;

  if (locator.context_before) {
    const ctx = locator.context_before.trim().toLowerCase();
    const textBefore = combinedText.slice(Math.max(0, matchStart - ctx.length - 20), matchStart).toLowerCase();
    if (textBefore.includes(ctx)) score += 2;
    else if (ctx.length > 10 && textBefore.includes(ctx.slice(-10))) score += 1;
  }

  if (locator.context_after) {
    const ctx = locator.context_after.trim().toLowerCase();
    const textAfter = combinedText.slice(matchEnd, matchEnd + ctx.length + 20).toLowerCase();
    if (textAfter.includes(ctx)) score += 2;
    else if (ctx.length > 10 && textAfter.includes(ctx.slice(0, 10))) score += 1;
  }

  return score;
};

const findAllOccurrences = (haystack: string, needle: string): number[] => {
  const indices: number[] = [];
  const lowerHaystack = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let pos = 0;
  while (pos < lowerHaystack.length) {
    const idx = lowerHaystack.indexOf(lowerNeedle, pos);
    if (idx < 0) break;
    indices.push(idx);
    pos = idx + 1;
  }
  return indices;
};

/** From a list of candidate char-index ranges, pick the best one using:
 *  1. Context score (highest wins)
 *  2. Tie-breaker: closest to locator start_char (if present)
 *  3. Confidence threshold: if best score is 0 and multiple candidates exist, return null */
const pickBestCandidate = (
  candidates: Array<{ charStart: number; charEnd: number }>,
  combinedText: string,
  segments: PrebuiltSegments["segments"],
  finding: FactCheckFinding
): { from: number; to: number } | null => {
  if (candidates.length === 0) return null;

  // Single candidate — use directly (no ambiguity)
  if (candidates.length === 1) {
    const c = candidates[0];
    const from = charIndexToPos(segments, c.charStart, "start");
    const to = charIndexToPos(segments, c.charEnd, "end");
    return (from && to && from < to) ? { from, to } : null;
  }

  // Multiple candidates — score and rank
  const scored = candidates.map(c => {
    const from = charIndexToPos(segments, c.charStart, "start");
    const to = charIndexToPos(segments, c.charEnd, "end");
    const score = scoreContextMatch(combinedText, c.charStart, c.charEnd, finding.locator);
    return { from, to, score, charStart: c.charStart };
  }).filter(c => c.from && c.to && c.from < c.to);

  if (scored.length === 0) return null;

  // Sort: highest score first, then closest to locator start_char as tie-breaker
  const locatorStart = finding.locator?.start_char != null ? Number(finding.locator.start_char) : null;
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (locatorStart != null && Number.isFinite(locatorStart)) {
      return Math.abs(a.charStart - locatorStart) - Math.abs(b.charStart - locatorStart);
    }
    return 0;
  });

  const best = scored[0];

  // When there is no locator but section_heading is present (agent-proposed
  // findings), use the heading position to pick the occurrence that belongs
  // to that section rather than always falling back to the first one.
  if (best.score === 0 && scored.length > 1 && !finding.locator && finding.section_heading) {
    const headingText = finding.section_heading.trim().replace(/^#+\s*/, "").toLowerCase();
    const headingIdx = combinedText.toLowerCase().indexOf(headingText);
    if (headingIdx >= 0) {
      const afterHeading = scored.filter(c => (c.charStart ?? 0) >= headingIdx);
      const pick = afterHeading[0] ?? scored[0];
      return { from: pick.from!, to: pick.to! };
    }
  }

  // Confidence threshold: if best score is 0 and there are multiple candidates,
  // we can't confidently pick one — return null to avoid risky apply.
  // Exception: if finding has no locator at all, context scoring is impossible
  // so we fall back to first occurrence rather than showing nothing.
  if (best.score === 0 && scored.length > 1 && finding.locator) {
    return null;
  }

  return { from: best.from!, to: best.to! };
};

const findRangeForFinding = (
  editor: any,
  finding: FactCheckFinding,
  prebuiltSegments?: PrebuiltSegments,
  // Once any apply/remove has happened in this session, backend-supplied
  // start_char/end_char on every other finding are off by the doc-length delta.
  // The text-validation guard catches most of those, but coincidental matches
  // at a stale offset can land in the wrong place. Caller passes true to skip
  // Strategy 1 entirely after the first apply.
  skipLocatorOffsets?: boolean
): FactCheckDecorationRange | null => {
  const rawNeedle = normalizeUnicode((finding.original_text || "").trim());
  const tableNeedle = normalizeMarkdownTableText(rawNeedle);
  // When original_text is a full markdown link [label](url), the editor renders
  // only the label text — strip link/image syntax so the highlight can land on
  // the visible text.
  const visibleNeedle = normalizeUnicode(rawNeedle.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1").trim());
  const needleVariants = Array.from(new Set([rawNeedle, tableNeedle, visibleNeedle].filter(Boolean)));
  if (needleVariants.length === 0) return null;

  let segments: PrebuiltSegments["segments"];
  let combinedText: string;
  if (prebuiltSegments) {
    // Caller already built segments and normalized the text
    segments = prebuiltSegments.segments;
    combinedText = prebuiltSegments.combinedText;
  } else {
    const built = buildTextSegments(editor);
    if (!built.combinedText) return null;
    segments = built.segments;
    combinedText = normalizeUnicode(built.combinedText);
  }

  // ── Strategy 1: Locator offsets ──────────────────────────────────────
  // Backend computes start_char/end_char against plain text (same space as
  // ProseMirror text nodes). Validate the text at the offset before trusting it.
  const locator = finding.locator;
  // Coerce to numbers — DynamoDB / JSON may return strings like "1804"
  const rawStartChar = locator?.start_char != null ? Number(locator.start_char) : NaN;
  const rawEndChar = locator?.end_char != null ? Number(locator.end_char) : NaN;
  if (
    !skipLocatorOffsets &&
    Number.isFinite(rawStartChar) &&
    Number.isFinite(rawEndChar) &&
    rawStartChar < rawEndChar
  ) {
    const startChar = rawStartChar;
    const endChar = rawEndChar;
    const textAtOffset = combinedText.slice(startChar, endChar);
    if (textAtOffset && needleVariants.some((variant) => isCloseMatch(textAtOffset, variant))) {
      const from = charIndexToPos(segments, startChar, "start");
      const to = charIndexToPos(segments, endChar, "end");
      if (from && to && from < to) {
        return { from, to, severity: finding.severity, findingId: finding.id };
      }
    }
  }

  // ── Strategy 2: Exact text match ─────────────────────────────────────
  for (const needle of needleVariants) {
    const occurrences = findAllOccurrences(combinedText, needle);
    if (occurrences.length === 0) continue;
    const result = pickBestCandidate(
      occurrences.map(idx => ({ charStart: idx, charEnd: idx + needle.length })),
      combinedText, segments, finding
    );
    if (result) return { ...result, severity: finding.severity, findingId: finding.id };
  }

  // Normalized whitespace fallback — find all occurrences, pick best by context
  for (const needle of needleVariants) {
    const normalizedHaystack = buildNormalizedTextMap(combinedText);
    const normalizedNeedle = buildNormalizedTextMap(needle).normalized;
    if (!normalizedNeedle) continue;

    const normCandidates: Array<{ charStart: number; charEnd: number }> = [];
    let searchPos = 0;
    while (searchPos < normalizedHaystack.normalized.length) {
      const normalizedIndex = normalizedHaystack.normalized.indexOf(normalizedNeedle, searchPos);
      if (normalizedIndex < 0) break;
      const rawStart = normalizedHaystack.normalizedToRaw[normalizedIndex];
      const rawEndMapIndex = normalizedIndex + normalizedNeedle.length - 1;
      const rawEndCharIndex = normalizedHaystack.normalizedToRaw[rawEndMapIndex];
      if (Number.isFinite(rawStart) && Number.isFinite(rawEndCharIndex)) {
        normCandidates.push({ charStart: rawStart, charEnd: rawEndCharIndex + 1 });
      }
      searchPos = normalizedIndex + 1;
    }

    if (normCandidates.length > 0) {
      const result = pickBestCandidate(normCandidates, combinedText, segments, finding);
      if (result) return { ...result, severity: finding.severity, findingId: finding.id };
    }
  }

  // ── Strategy 2.5: Head-tail anchor matching ─────────────────────────
  // When Claude slightly misquotes text (missing/extra words), split the
  // needle into head and tail portions and find the spanning region.
  for (const needle of needleVariants) {
    const words = needle.split(/\s+/);
    if (words.length >= 3) {
      const midpoint = Math.ceil(words.length / 2);
      const headPhrase = words.slice(0, midpoint).join(" ");
      const tailPhrase = words.slice(-Math.max(1, Math.floor(words.length / 3))).join(" ");

      // Find all head occurrences, then match corresponding tails
      const headOccurrences = findAllOccurrences(combinedText, headPhrase);
      const htCandidates: Array<{ charStart: number; charEnd: number }> = [];
      for (const headIdx of headOccurrences) {
        const searchAfter = headIdx + headPhrase.length;
        const tailWindow = combinedText.slice(searchAfter, searchAfter + needle.length * 2);
        const tailIdx = tailWindow.toLowerCase().indexOf(tailPhrase.toLowerCase());
        if (tailIdx >= 0) {
          htCandidates.push({ charStart: headIdx, charEnd: searchAfter + tailIdx + tailPhrase.length });
        }
      }

      if (htCandidates.length > 0) {
        const result = pickBestCandidate(htCandidates, combinedText, segments, finding);
        if (result) return { ...result, severity: finding.severity, findingId: finding.id };
      }
    }
  }

  // ── Strategy 3: Context-anchored search ──────────────────────────────
  // Use context_before to locate the approximate region, then search for
  // the needle nearby. Collect all candidates across chunks, then pick best.
  if (locator?.context_before) {
    const ctxBefore = locator.context_before.trim();
    const chunks = [ctxBefore];
    // Extract last clause after a delimiter (handles markdown list → block rendering mismatch)
    const lastDelim = Math.max(ctxBefore.lastIndexOf(","), ctxBefore.lastIndexOf("."), ctxBefore.lastIndexOf(";"));
    if (lastDelim > 0 && ctxBefore.length - lastDelim > 5) {
      chunks.push(ctxBefore.slice(lastDelim + 1).trim());
    }
    if (ctxBefore.length > 20) {
      chunks.push(ctxBefore.slice(-20).trim());
    }

    const ctxCandidates: Array<{ charStart: number; charEnd: number }> = [];
    const seenRanges = new Set<string>();

    for (const chunk of chunks) {
      if (chunk.length < 5) continue;
      // Find all context_before occurrences, not just the first
      const ctxOccurrences = findAllOccurrences(combinedText, chunk);
      for (const ctxIndex of ctxOccurrences) {
        const searchStart = ctxIndex + chunk.length;
        const maxNeedleLength = Math.max(...needleVariants.map((n) => n.length));
        const searchWindow = combinedText.slice(searchStart, searchStart + maxNeedleLength * 2 + 100);

        // Try exact needle match in window for each variant
        for (const needle of needleVariants) {
          const needleInWindow = searchWindow.toLowerCase().indexOf(needle.toLowerCase());
          if (needleInWindow >= 0) {
            const absoluteStart = searchStart + needleInWindow;
            const key = `${absoluteStart}:${absoluteStart + needle.length}`;
            if (!seenRanges.has(key)) {
              seenRanges.add(key);
              ctxCandidates.push({ charStart: absoluteStart, charEnd: absoluteStart + needle.length });
            }
          }
        }

        // Try head-tail anchor within the context window for each needle variant
        for (const needle of needleVariants) {
          const words = needle.split(/\s+/);
          if (words.length >= 3) {
            const midpoint = Math.ceil(words.length / 2);
            const headPhrase = words.slice(0, midpoint).join(" ");
            const tailPhrase = words.slice(-Math.max(1, Math.floor(words.length / 3))).join(" ");

            const headInWindow = searchWindow.toLowerCase().indexOf(headPhrase.toLowerCase());
            if (headInWindow >= 0) {
              const afterHead = headInWindow + headPhrase.length;
              const tailInWindow = searchWindow.slice(afterHead, afterHead + needle.length * 2).toLowerCase().indexOf(tailPhrase.toLowerCase());
              if (tailInWindow >= 0) {
                const absStart = searchStart + headInWindow;
                const absEnd = searchStart + afterHead + tailInWindow + tailPhrase.length;
                const key = `${absStart}:${absEnd}`;
                if (!seenRanges.has(key)) {
                  seenRanges.add(key);
                  ctxCandidates.push({ charStart: absStart, charEnd: absEnd });
                }
              }
            }
          }
        }
      }
    }

    if (ctxCandidates.length > 0) {
      const result = pickBestCandidate(ctxCandidates, combinedText, segments, finding);
      if (result) return { ...result, severity: finding.severity, findingId: finding.id };
    }
  }


  return null;
};

/** Resolve the editor range for an *applied* finding using its post-apply
 *  text + context, not the (preserved-as-audit) `original_text`. Reuses the
 *  same matcher with a synthetic finding shape; locator carries no offsets
 *  by design (offsets go stale instantly on any neighbouring edit). */
const findRangeForAppliedFinding = (
  editor: any,
  finding: FactCheckFinding,
  prebuiltSegments?: PrebuiltSegments
): FactCheckDecorationRange | null => {
  if (!finding.applied_text) return null;
  const synthetic: FactCheckFinding = {
    ...finding,
    original_text: finding.applied_text,
    locator: finding.applied_locator
      ? {
          context_before: finding.applied_locator.context_before,
          context_after: finding.applied_locator.context_after,
        }
      : undefined,
  };
  // Always skip Strategy 1 — applied_locator never carries offsets.
  return findRangeForFinding(editor, synthetic, prebuiltSegments, true);
};

const ArticleEditorComponent: React.FC<ArticleEditorComponentProps> = ({
  id,
  title = "Default Title",
  description = "Default Description",
  isOptimizedMode = false,
  hideArticle = false,
  fetchContent,
  saveContent,
  additionalProps = {},
  isEEATMode = false,
}) => {
  const dispatch = useDispatch();
  const isHumanized = useSelector(selectIsHumanized);
  const currentPlan: any = useSelector(selectCurrentPlan);
  const isImageGenEnabled = currentPlan?.spreadsheet === "true" || currentPlan?.spreadsheet === true;
  const [showAIDropdown, setShowAIDropdown] = useState(false);
  const [selectedText, setSelectedText] = useState<SelectedTextItem[]>([]);
  const [shouldShowBubbleMenu, setShouldShowBubbleMenu] = useState(true);
  const [bottomMargin, setBottomMargin] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isWaitingForFirstChunk, setIsWaitingForFirstChunk] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isOptimizeContentDialogOpen, setIsOptimizeContentDialogOpen] =
    useState(false);
  const [cords, setCords] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  // Add state for selection coordinates to use in fixed positioning
  const [selectionCoords, setSelectionCoords] = useState({
    startCoords: { left: 0, right: 0, top: 0, bottom: 0 },
    endCoords: { left: 0, right: 0, top: 0, bottom: 0 },
  });
  const [html, setHtml] = useState("");
  const [humanizedHtml, setHumanizedHtml] = useState("");
  const htmlRef = useRef("");
  const humanizedHtmlRef = useRef("");
  const isContentLoaded = useRef(false);
  const [editorContent, setEditorContent] = useState<string>("");
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [isValidEvaluationData, setIsValidEvaluationData] =
    useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [truncatedContent, setTruncatedContent] = useState("");
  const [isEditingHumanizedVersion, setIsEditingHumanizedVersion] =
    useState(false);
  const [isOptimizeContentDialogLoading, setIsOptimizeContentDialogLoading] =
    useState(false);
  const [articleLink, setArticleLink] = useState<string | null>(null);
  const [useScrollBasedTruncation, setUseScrollBasedTruncation] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isWritingMode, setIsWritingMode] = useState(false);
  const [factCheckFindings, setFactCheckFindings] = useState<FactCheckFinding[]>([]);
  // Mirror of factCheckFindings in a ref so async callbacks (e.g. the apply
  // handler) can read the latest list without waiting for a re-render. State
  // closures would otherwise capture a stale snapshot if findings changed
  // between user click and the callback firing.
  const factCheckFindingsRef = useRef<FactCheckFinding[]>([]);
  const [factCheckAutoReview, setFactCheckAutoReview] = useState<AutoReview | null>(null);
  const [activeFindingId, setActiveFindingId] = useState<number | null>(null);
  // True while a /article/apply-finding call is in flight. Drives the
  // sidebar's disabled state AND short-circuits decline (defense in depth).
  const [isApplyingFactCheck, setIsApplyingFactCheck] = useState(false);
  const isApplyingFactCheckRef = useRef(false);
  const [showImageGenModal, setShowImageGenModal] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [imageGenError, setImageGenError] = useState<string | null>(null);
  const converter = getMarkdownConverter();
  const router = useRouter();

  // Refs to track programmatic changes and content saving
  const isContentSaving = React.useRef(false);
  const lastSavedContent = React.useRef("");
  const isProgrammaticChange = React.useRef(false);

  // Create local ref to track which version we're currently editing (humanized or original)
  const isEditingHumanizedContent = React.useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sequence counter to prevent out-of-order toggle writes (Fix P1)
  const toggleSequence = React.useRef<number>(0);

  // Debounced save for fact-check finding statuses.
  // Writes are serialized so stale in-flight requests cannot overwrite newer state.
  const findingsSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest unsent args captured outside the timer's closure so flush can drain
  // them immediately if the debounce hasn't fired yet.
  const pendingFindingsArgs = React.useRef<{ findings: FactCheckFinding[]; content: string } | null>(null);
  // Promise ref (rather than a bare boolean) so flushPendingFindingsSave can
  // await the in-flight loop instead of polling.
  const findingsInFlightPromise = React.useRef<Promise<void> | null>(null);
  const decorationRunId = React.useRef(0);
  const lastPrebuiltRef = React.useRef<PrebuiltSegments | null>(null);
  // Once any apply/remove fires, every other finding's locator.start_char/end_char
  // is shifted by the doc-length delta. Skip Strategy 1 in the resolver after the
  // first apply, falling through to text + context matching.
  const localOffsetsDirtyRef = React.useRef(false);
  // Tracks the set of finding IDs we've seen so we can detect a fresh load
  // or a fresh fact-check pass. When the ID set changes, the new findings
  // came with offsets computed against current content — trust them again.
  // Apply/decline updates keep the same IDs, so they don't trigger a reset.
  const previousFindingIdsRef = React.useRef<string>("");
  const queuedFindingsPayload = React.useRef<{ findings: FactCheckFinding[]; content: string } | null>(null);
  const persistFactCheckFindings = useCallback(async (snapshot: FactCheckFinding[], contentSnapshot: string) => {
    queuedFindingsPayload.current = { findings: snapshot, content: contentSnapshot };
    if (findingsInFlightPromise.current) return findingsInFlightPromise.current;

    const promise = (async () => {
      try {
        while (queuedFindingsPayload.current) {
          const payload = queuedFindingsPayload.current;
          queuedFindingsPayload.current = null;

          try {
            const response = await fetch(`${getArticleApiBaseUrl()}/article/update`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                briefId: id,
                content: convertToMarkdown(payload.content),
                isHumanized: isEditingHumanizedContent.current ?? false,
                isEdited: false,
                factCheckFindings: payload.findings,
              }),
            });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
          } catch (err) {
            console.error("[FactCheck] Failed to save finding statuses:", err);
          }
        }
      } finally {
        findingsInFlightPromise.current = null;
      }
    })();
    findingsInFlightPromise.current = promise;
    return promise;
  }, [id]);
  const saveFactCheckFindings = useCallback((updatedFindings: FactCheckFinding[], contentSnapshot: string) => {
    // Stash args outside the timer's closure so flushPendingFindingsSave can
    // drain whatever was last requested even if the debounce hasn't fired.
    pendingFindingsArgs.current = { findings: updatedFindings, content: contentSnapshot };
    if (findingsSaveTimer.current) clearTimeout(findingsSaveTimer.current);
    findingsSaveTimer.current = setTimeout(() => {
      const args = pendingFindingsArgs.current;
      pendingFindingsArgs.current = null;
      findingsSaveTimer.current = null;
      if (args) persistFactCheckFindings(args.findings, args.content);
    }, 500);
  }, [persistFactCheckFindings]);

  // Drain any pending or in-flight fact-check status save before doing
  // something that depends on the article content (e.g. backend apply).
  // Without this, a queued decline/status save can land *after* the apply
  // and overwrite the applied content with pre-apply markdown.
  const flushPendingFindingsSave = useCallback(async () => {
    if (findingsSaveTimer.current) {
      clearTimeout(findingsSaveTimer.current);
      findingsSaveTimer.current = null;
    }
    const args = pendingFindingsArgs.current;
    pendingFindingsArgs.current = null;
    if (args) {
      // persistFactCheckFindings returns a promise that resolves when the
      // queue is fully drained (including any save kicked off just now).
      await persistFactCheckFindings(args.findings, args.content);
    } else if (findingsInFlightPromise.current) {
      await findingsInFlightPromise.current;
    }
  }, [persistFactCheckFindings]);

  // Note: Scroll detection no longer needed since unlock card is embedded in content

  // Debounce the save content function with dual channels (Humanized vs Original)
  const { debounce: debouncedSaveContent, flush: flushPendingSaves } = useDualDebounce(
    (content: string, options = {}) => handleSaveContent(content, options),
    1000
  );
  // Keep the findings ref in sync so async handlers can read the freshest
  // list without depending on the React render cycle.
  useEffect(() => {
    factCheckFindingsRef.current = factCheckFindings;
  }, [factCheckFindings]);

  const turndownService = setupTurndownService();
  const editor = useEditor(
    {
      extensions: getEditorExtensions(),
      onCreate({ editor }) {
        editor.view.dom.addEventListener(
          "paste",
          (event) => {
            if (document.activeElement?.closest(".MuiTooltip-popper")) {
              event.stopPropagation();
            }
          },
          true
        );
        lastSavedContent.current = editor.getHTML();
        // Initialize word count
        setWordCount(getWordCount(editor.getText()));
      },
      content: editorContent,
      onUpdate: ({ editor }) => {
        // User typed — cached text segments are stale.
        lastPrebuiltRef.current = null;

        if (isProgrammaticChange.current) {
          return;
        }
        const currentContent = editor.getHTML();

        // Update word count
        setWordCount(getWordCount(editor.getText()));

        if (currentContent !== lastSavedContent.current) {
          // Use our local ref to track which version we're saving
          const saveOptions = {
            isEdited: true,
            isHumanized: isEditingHumanizedContent.current ?? false,
          };

          // Debounce the save operation to avoid excessive API calls
          debouncedSaveContent(currentContent, saveOptions);
          // Don't update lastSavedContent here - wait for successful save in handleSaveContent
        }
      },
    },
    [editorContent]
  );

  // Function to load content
  const getNormalizedFactCheckFindings = (data: any): FactCheckFinding[] => {
    const parseIfJson = (value: any) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizedData = unwrapDynamoValue(data);
    const candidates = [
      normalizedData?.factCheckFindings,
      normalizedData?.fact_check_findings,
      normalizedData?.factCheck?.findings,
      normalizedData?.fact_check?.findings,
      normalizedData?.factCheckResult?.findings,
      normalizedData?.fact_check_result?.findings,
      normalizedData?.factCheck,
      normalizedData?.fact_check,
    ];

    const rawFindings = candidates
      .map(parseIfJson)
      .map(unwrapDynamoValue)
      .find((entry) => Array.isArray(entry));

    if (!Array.isArray(rawFindings)) {
      return [];
    }

    return rawFindings
      .filter((item) => item && typeof item === "object")
      .map((item: any, index: number): FactCheckFinding => ({
        id: Number.isFinite(Number(item.id)) ? Number(item.id) : index + 1,
        section_heading: String(item.section_heading ?? item.sectionHeading ?? "Introduction"),
        category: String(item.category ?? "unverifiable_claim"),
        severity: String(item.severity ?? "warning"),
        original_text: String(item.original_text ?? item.originalText ?? ""),
        replacement_text:
          item.replacement_text === null || item.replacementText === null
            ? null
            : String(item.replacement_text ?? item.replacementText ?? ""),
        reasoning: String(item.reasoning ?? ""),
        source: String(item.source ?? ""),
        action: String(item.action ?? "flag_for_review"),
        applied: item.applied ?? item.status === "applied",
        status: item.status || (item.applied ? "applied" : "pending"),
        locator: item.locator,
        applied_text:
          typeof item.applied_text === "string"
            ? item.applied_text
            : typeof item.appliedText === "string"
              ? item.appliedText
              : undefined,
        applied_locator: item.applied_locator ?? item.appliedLocator ?? undefined,
        // Round-trip the agent-proposed marker fields so the load-time merge
        // can dedupe entries the backend has already persisted.
        agentProposedKey:
          typeof item.agentProposedKey === "string" ? item.agentProposedKey : undefined,
        causedBy:
          typeof item.causedBy === "number"
            ? item.causedBy
            : Number.isFinite(Number(item.causedBy))
              ? Number(item.causedBy)
              : undefined,
        evidenceUrls: Array.isArray(item.evidenceUrls) ? item.evidenceUrls : undefined,
      }))
      .filter((finding) => finding.original_text.trim().length > 0);
  };

  // Promote `factCheckAutoReview.proposedNewFindings` into the canonical
  // findings list. They become first-class findings — visible in the editor
  // (highlighted by `applyFactCheckDecorations`) and applyable via the
  // existing `/article/apply-finding` flow (the backend looks them up by id
  // in the client-supplied `currentFindings`). Idempotent across reloads:
  // once a proposed finding has been applied or declined, the backend
  // persists it inside `factCheckFindings`; the dedup below skips it on the
  // next merge.
  const mergeAgentProposedIntoFindings = (
    findings: FactCheckFinding[],
    autoReview: AutoReview | null,
  ): FactCheckFinding[] => {
    const proposed = autoReview?.proposedNewFindings ?? [];
    if (proposed.length === 0) return findings;

    // Check 1 — persisted findings (backward-compat with old "causedBy|original_text"
    // key format AND new "causedBy|section|original_text" format). Section-agnostic:
    // if any persisted finding covers this original_text, bulk-replace already
    // fixed all occurrences, so skip all proposed entries for it.
    const seenOriginal = new Set(
      findings
        .filter((f) => f.category === "agent_proposed" && typeof f.agentProposedKey === "string")
        .map((f) => {
          const parts = (f.agentProposedKey as string).split("|");
          // New format: "causedBy|section_heading|original_text" (3+ parts)
          // Strip section to get the section-agnostic key for matching.
          return parts.length >= 3
            ? `${parts[0]}|${parts.slice(2).join("|")}`
            : (f.agentProposedKey as string);
        }),
    );

    // Check 2 — within-batch dedup: same section + same text = true duplicate.
    const seenInBatch = new Set<string>();

    const baseId = findings.reduce((m, f) => Math.max(m, f.id ?? 0), 0);
    const merged = [...findings];

    proposed.forEach((p, idx) => {
      const originalKey = `${p.causedBy ?? "x"}|${p.original_text}`;
      if (seenOriginal.has(originalKey)) return;

      const key = `${p.causedBy ?? "x"}|${p.section_heading}|${p.original_text}`;
      if (seenInBatch.has(key)) return;
      seenInBatch.add(key);
      merged.push({
        id: baseId + 1 + idx,
        section_heading: p.section_heading,
        category: "agent_proposed",
        severity: p.severity,
        original_text: p.original_text,
        replacement_text: p.replacement_text || null,
        reasoning: p.reasoning,
        source: "",
        action: p.action,
        status: "pending",
        agentProposedKey: key,
        causedBy: p.causedBy,
        evidenceUrls: Array.isArray(p.evidenceUrls)
          ? p.evidenceUrls.map((u: string) => (u.startsWith("http") ? u : `https://${u}`))
          : [],
      });
    });

    return merged;
  };

  const getNormalizedFactCheckAutoReview = (data: any): AutoReview | null => {
    const parseIfJson = (value: any) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizedData = unwrapDynamoValue(data);
    const candidates = [
      normalizedData?.factCheckAutoReview,
      normalizedData?.fact_check_auto_review,
      normalizedData?.factCheckReview,
    ];

    const raw = candidates
      .map(parseIfJson)
      .map(unwrapDynamoValue)
      .find((entry) => entry && typeof entry === "object" && !Array.isArray(entry));

    if (!raw || typeof raw !== "object") return null;

    return {
      status: raw.status,
      completedAt: raw.completedAt,
      decisions: Array.isArray(raw.decisions) ? raw.decisions : [],
      proposedNewFindings: Array.isArray(raw.proposedNewFindings) ? raw.proposedNewFindings : [],
      summary: raw.summary || {},
    };
  };

  const loadContent = async () => {
    try {
      // Reset loaded flag to prevent effects from running with stale refs during fetch
      isContentLoaded.current = false;
      setIsLoading(true);
      const data = await fetchContent(id);

      // Process the content based on the data format returned
      if (data) {
        const contentHtml = converter.makeHtml(data.content || "");
        const humanizedHtml = converter.makeHtml(data.humanizedContent || "");

        setHtml(contentHtml);
        setHumanizedHtml(humanizedHtml);
        htmlRef.current = contentHtml;
        humanizedHtmlRef.current = humanizedHtml;
        isContentLoaded.current = true;

        // Set both states based on the data
        dispatch(setIsHumanized(data.isHumanized));
        dispatch(setShowHumanizeContent(data.isHumanized));

        // Initialize editing state
        isEditingHumanizedContent.current = data.isHumanized;
        setIsEditingHumanizedVersion(data.isHumanized);

        // Set the initial content
        const initialContent = data.isHumanized ? humanizedHtml : contentHtml;

        // Initialize word count from HTML content (which will be displayed in editor)
        // Create a temporary div to extract text from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = initialContent;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        const initialWordCount = getWordCount(textContent);
        setWordCount(initialWordCount);

        // Set the initial content - truncation will be handled in the truncation effect
        setEditorContent(initialContent);
        handleSaveEvaluationData(data);

        // Set the article link
        if (data.articleLink) {
          setArticleLink(data.articleLink);
        }

        const baseFindings = getNormalizedFactCheckFindings(data);
        const autoReview = getNormalizedFactCheckAutoReview(data);
        setFactCheckFindings(mergeAgentProposedIntoFindings(baseFindings, autoReview));
        setFactCheckAutoReview(autoReview);
      }
    } catch (error) {
      dispatch(
        snackAdded({
          message: `Failed to load content`,
          type: "error",
        })
      );
      setFactCheckFindings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEvaluationData = async (data: any) => {
    // Process and validate the evaluationData
    try {
      let isValid = false;

      if (data.articleEEATScore) {
        try {
          // Get articles and analysis
          let articles = data.articleEEATScore.articles;
          let analysis = data.articleEEATScore.analysis;
          let suggestions = data.articleEEATScore.suggestions;

          // Parse if needed
          const parsedArticles =
            typeof articles === "string" ? JSON.parse(articles) : articles;
          const parsedAnalysis =
            typeof analysis === "string" ? JSON.parse(analysis) : analysis;

          // Check for required structure in articles
          if (
            !parsedArticles?.original?.scores ||
            !parsedArticles?.updated?.scores ||
            !parsedArticles?.original?.overall ||
            !parsedArticles?.updated?.overall
          ) {
            throw new Error("Articles data is missing required structure");
          }

          // Verify scores contain all required metrics
          const originalScores = parsedArticles.original.scores;
          const updatedScores = parsedArticles.updated.scores;

          if (
            typeof originalScores.experience !== "number" ||
            typeof originalScores.expertise !== "number" ||
            typeof originalScores.authoritativeness !== "number" ||
            typeof originalScores.trustworthiness !== "number"
          ) {
            throw new Error("Original scores are missing required metrics");
          }

          if (
            typeof updatedScores.experience !== "number" ||
            typeof updatedScores.expertise !== "number" ||
            typeof updatedScores.authoritativeness !== "number" ||
            typeof updatedScores.trustworthiness !== "number"
          ) {
            throw new Error("Updated scores are missing required metrics");
          }

          // Check analysis structure
          if (
            !parsedAnalysis?.experience?.comparison ||
            !parsedAnalysis?.expertise?.comparison ||
            !parsedAnalysis?.authoritativeness?.comparison ||
            !parsedAnalysis?.trustworthiness?.comparison ||
            !Array.isArray(parsedAnalysis?.summary)
          ) {
            throw new Error("Analysis is missing required structure");
          }

          // If we reach here without error, data is valid
          isValid = true;
        } catch (error) {
          console.error("Invalid EEAT score data:", error);
          isValid = false;
        }
      } else {
        isValid = true;
      }

      setEvaluationData({ articleEEATScore: data.articleEEATScore });
      setIsValidEvaluationData(isValid);
    } catch (error) {
      console.error("Failed to save evaluation data:", error);
    }
  };

  function convertToMarkdown(content: string) {
    // Add color span rule
    turndownService.addRule("colorSpan", {
      filter: function (node) {
        return node.nodeName === "SPAN" && node.style && node.style.color;
      },
      replacement: function (content, node) {
        const color = node.style.color;
        return `<span style="color: ${color};">${content}</span>`;
      },
    });

    return turndownService.turndown(content);
  }
  // Function to save content
  const handleSaveContent = async (content: string, options: any) => {
    // Mark that we are currently saving so other updates can be deferred
    isContentSaving.current = true;

    const markdownContent = convertToMarkdown(content);

    // Calculate character count from markdown content
    const charCount = markdownContent.length;

    try {
      const result = await saveContent(markdownContent, { ...options, charCount });

      // Update refs only (not state) to avoid triggering effects that reset cursor
      if (options.isHumanized) {
        humanizedHtmlRef.current = content;
      } else {
        htmlRef.current = content;
      }

      // Update the reference of the last successfully saved content
      lastSavedContent.current = content;

      return result;
    } catch (error) {
      dispatch(
        snackAdded({
          message: "Failed to save changes",
          type: "error",
        })
      );
      throw error;
    } finally {
      // Reset the saving flag regardless of success or failure
      isContentSaving.current = false;
    }
  };

  // Load content when component mounts or ID changes
  useEffect(() => {
    if (id) {
      loadContent();
    }
  }, [id]);

  // Handle content truncation for preview mode
  useEffect(() => {
    if (!editor || !hideArticle) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Wait for content to be loaded (use ref instead of state to avoid re-triggering on save)
    if (!isContentLoaded.current) {
      return;
    }

    const content = editor.getHTML();

    if (content.length < 100) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Use TipTap commands to insert unlock card after 4 paragraphs
    setUseScrollBasedTruncation(true);

    isProgrammaticChange.current = true;
    insertUnlockCardWithTipTap(editor, content, isOptimizedMode);
    isProgrammaticChange.current = false;
    setTruncatedContent(content);
  }, [hideArticle, editor]);


  // Helper functions
  const selectedWords = () => {
    return countSelectedWords(editor);
  };

  const handleAddMetaToArticle = (html: string) => {
    return addMetaToArticle(html, title, description);
  };

  const insertImageIntoEditor = (
    editorInstance: any,
    imageUrl: string,
    altText?: string,
    placementContext?: { method: string; identifier: string },
    isCover?: boolean
  ) => {
    if (!editorInstance) return;

    // Cover image: insert before the first H1
    if (isCover) {
      let h1Pos: number | null = null;
      editorInstance.state.doc.descendants((node: any, pos: number) => {
        if (h1Pos !== null) return false;
        if (node.type.name === "heading" && node.attrs.level === 1) {
          h1Pos = pos;
          return false;
        }
      });
      if (h1Pos !== null) {
        editorInstance
          .chain()
          .focus()
          .insertContentAt(h1Pos, {
            type: "image",
            attrs: { src: imageUrl, alt: altText || "" },
          })
          .run();
        return;
      }
    }

    // Contextual placement based on identifier
    if (placementContext?.identifier) {
      let targetPos: number | null = null;
      const needle = placementContext.identifier.trim().toLowerCase();
      const isHeading = placementContext.method === "inline_after_heading";
      const targetType = isHeading ? "heading" : "paragraph";

      editorInstance.state.doc.descendants((node: any, pos: number) => {
        if (targetPos !== null) return false;
        if (
          node.type.name === targetType &&
          node.textContent.trim().toLowerCase().includes(needle)
        ) {
          targetPos = pos + node.nodeSize;
          return false;
        }
      });

      if (targetPos !== null) {
        editorInstance
          .chain()
          .focus()
          .setTextSelection(targetPos)
          .setImage({ src: imageUrl, alt: altText || "" })
          .run();
        return;
      }
    }

    // Fallback: insert at end of document
    const endPos = editorInstance.state.doc.content.size;
    editorInstance
      .chain()
      .focus()
      .setTextSelection(endPos)
      .setImage({ src: imageUrl, alt: altText || "" })
      .run();
  };

  const handleGenerateImages = async () => {
    setShowImageGenModal(true);
    setImageGenLoading(true);
    setImageGenError(null);

    try {
      const articleContent = editor?.getHTML();
      if (!articleContent) {
        setImageGenError("No article content to analyze");
        setImageGenLoading(false);
        return;
      }

      // Fetch brand guidelines for the project (if available)
      let brandGuidelines = "";
      try {
        const briefData = await API.get("brief", `/briefs/object/${id}`, {});
        if (briefData?.projectId) {
          const guidelinesRes = await API.get("projects", `/projects/getBrandStyleGuidelines/${briefData.projectId}`, {});
          if (guidelinesRes?.success && guidelinesRes?.guidelines) {
            brandGuidelines = guidelinesRes.guidelines;
          }
        }
      } catch (err) {
        console.warn("Could not fetch brand guidelines, proceeding without:", err);
      }

      // Call image-generation Function URL directly to avoid API Gateway 29s timeout
      const { SignatureV4 } = require("@aws-sdk/signature-v4");
      const { Sha256 } = require("@aws-crypto/sha256-browser");
      const { HttpRequest } = require("@aws-sdk/protocol-http");

      const lambdaUrl = new URL(process.env.NEXT_PUBLIC_IMAGE_GENERATION_URL!);
      const credentials = await Auth.currentCredentials();
      const requestBody = JSON.stringify({
        article_content: articleContent,
        article_id: id,
        article_url: `article-${id}`,
        brand_style_guidelines: brandGuidelines,
      });

      const request = new HttpRequest({
        method: "POST",
        hostname: lambdaUrl.hostname,
        path: lambdaUrl.pathname,
        headers: {
          "Content-Type": "application/json",
          host: lambdaUrl.hostname,
        },
        body: requestBody,
      });

      const signer = new SignatureV4({
        credentials,
        region: process.env.NEXT_PUBLIC_AWS_REGION || "eu-central-1",
        service: "lambda",
        sha256: Sha256,
      });

      const signedRequest = await signer.sign(request);
      const { host, ...headersForRequest } = signedRequest.headers;

      const res = await fetch(
        process.env.NEXT_PUBLIC_IMAGE_GENERATION_URL!,
        {
          method: "POST",
          headers: headersForRequest,
          body: requestBody,
        }
      );

      const data = await res.json();
      const result =
        typeof data.body === "string" ? JSON.parse(data.body) : data.body || data;

      if (res.ok && result.opportunities) {
        // Map the response to match the modal's expected format
        // New structure: each opportunity has images[] array + image_url
        // The Lambda generates one image per opportunity, with the URL at opp.image_url
        // Each opp.images[] entry is a prompt definition — they share the same generated image_url
        const images: any[] = [];
        const opps = result.opportunities || [];

        for (const opp of opps) {
          const oppImages = opp.images || [opp]; // fallback for old flat format
          const imageUrl = opp.image_url || "";

          for (const img of oppImages) {
            images.push({
              success: !!imageUrl,
              image_url: imageUrl,
              metadata: {
                alt_text: img.alt_text || "",
                image_type: img.image_type || "",
                placement_context: img.placement_context || null,
                is_cover: img.is_cover || false,
                text_in_image: img.text_in_image || "",
                aspect_ratio: img.aspect_ratio || "",
              },
            });
          }
        }
        // Auto-insert all successful images into the editor
        const successfulImages = images.filter((img: any) => img.success && img.image_url);

        if (successfulImages.length > 0 && editor) {
          // Insert cover images first, then others
          const coverImages = successfulImages.filter((img: any) => img.metadata?.is_cover);
          const otherImages = successfulImages.filter((img: any) => !img.metadata?.is_cover);

          for (const img of [...coverImages, ...otherImages]) {
            insertImageIntoEditor(
              editor,
              img.image_url,
              img.metadata?.alt_text,
              img.metadata?.placement_context,
              img.metadata?.is_cover
            );
          }
        }

        setShowImageGenModal(false);
      } else {
        setImageGenError(result.error || "Image generation failed");
      }
    } catch (err: any) {
      console.error("Image generation error:", err);
      setImageGenError(err.message || "Failed to generate images");
    } finally {
      setImageGenLoading(false);
    }
  };

  const handleDownload = () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    let htmlToDownload = editor?.getHTML();
    const htmlWithMeta = handleAddMetaToArticle(htmlToDownload);
    Export2Docx(htmlWithMeta, title);

    // Reset download flag if using the optimized editor
    if (additionalProps.setIsDownloadContent) {
      additionalProps.setIsDownloadContent(false);
    }
  };

  const handleCopy = async () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    try {
      let htmlToCopy = editor?.getHTML();
      const htmlWithMeta = handleAddMetaToArticle(htmlToCopy);

      const success = await copyHtmlToClipboard(htmlWithMeta);
      if (success) {
        dispatch(
          snackAdded({
            message: "Content copied to clipboard!",
            type: "success",
            duration: 2000,
          })
        );
      } else {
        throw new Error("Clipboard copy failed");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      dispatch(
        snackAdded({
          message: "Failed to copy content. Please try again.",
          type: "error",
          duration: 1000,
        })
      );
    }

    // Reset copy flag if using the optimized editor
    if (additionalProps.setIsCopyContent) {
      additionalProps.setIsCopyContent(false);
    }
  };

  const handleAIDropdown = () => {
    if (isEEATMode) {
      dispatch(
        snackAdded({
          message:
            "You can't apply AI to an EEAT article. Please use Update Article to improve your article.",
          type: "info",
        })
      );
      return;
    }
    isProgrammaticChange.current = true;
    const { from, to } = editor.state.selection;
    editor
      ?.chain()
      .setTextSelection({ from, to })
      .focus()
      .setHighlight("lightgray")
      .run();
    isProgrammaticChange.current = false;

    let startCoords = editor.view.coordsAtPos(from);
    let endCoords = editor.view.coordsAtPos(to);

    // Set coordinates for the dropdown
    // Position it slightly below and to the left of the selected text
    // Use the end of the selection (where the cursor would be) as the anchor point
    setCords({
      top: endCoords.bottom + 10, // 10px below the selection
      left: endCoords.left - 20, // 20px to the left of the end of selection
      bottom: endCoords.bottom,
      right: endCoords.right,
    });

    if (!scrollContainerRef.current) {
      return;
    }

    // Find all possible scroll sources and use the maximum value
    const editorDOM = editor.view.dom;

    // Get all possible scroll values
    const windowScrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;
    const containerScrollTop = scrollContainerRef.current.scrollTop || 0;
    const editorScrollTop = editorDOM.scrollTop || 0;

    // Check for scrollable parent elements
    let parentScrollTop = 0;
    let currentElement = editorDOM.parentElement;
    let scrollableParent = null;

    while (currentElement && currentElement !== document.body) {
      if (
        currentElement.scrollHeight > currentElement.clientHeight &&
        currentElement.scrollTop > 0
      ) {
        scrollableParent = currentElement;
        parentScrollTop = currentElement.scrollTop;

        break;
      }
      currentElement = currentElement.parentElement;
    }

    // Use the maximum scroll value from any source
    const scrollY = Math.max(
      windowScrollY,
      containerScrollTop,
      editorScrollTop,
      parentScrollTop
    );

    // Store the effective scroll position for use in dropdown positioning
    const effectiveScrollPosition = scrollY;

    // Calculate position relative to the editor's position in the container
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const editorRect = editor.view.dom.getBoundingClientRect();

    // We will position the dropdown at the beginning of the last line of selected text
    // Use a safer approach to get text content
    const selectionText = editor.state.doc.textBetween(from, to);

    // Find the last newline in the selection
    const lastNewlineIndex = selectionText.lastIndexOf("\n");

    // Position at the start of the last line
    let lastLineStartPos = from;
    if (lastNewlineIndex !== -1) {
      // Found a newline, position after it (first character of last line)
      lastLineStartPos = from + lastNewlineIndex + 1;
    }

    // Get coordinates for the start of the last line
    let startOfLastLineCoords = editor.view.coordsAtPos(lastLineStartPos);

    // Calculate available space below the selection
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - endCoords.bottom;

    // Height of the dropdown (approximate)
    const dropdownHeight = 150; // Adjust based on your dropdown's actual height

    // Check if there's enough space below
    const positionAbove = spaceBelow < dropdownHeight;

    let relativeTop, relativeLeft;

    if (positionAbove) {
      // Position above the selection
      relativeTop =
        startOfLastLineCoords.top -
        containerRect.top +
        scrollY -
        dropdownHeight;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    } else {
      // Position below the selection (original behavior)
      relativeTop = startOfLastLineCoords.top - containerRect.top + scrollY;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    }

    // Set coordinates for the dropdown
    setCords({
      top: positionAbove ? relativeTop : relativeTop + 10, // If above, no extra padding
      left: relativeLeft,
      bottom: relativeTop,
      right: endCoords.right - containerRect.left,
    });

    // Extract selected blocks
    const selectedBlocks = extractSelectedBlocks(
      editor,
      editor.state.selection.from,
      editor.state.selection.to,
      converter
    );

    if (selectedWords() > 500) {
      dispatch(
        snackAdded({
          message: "Please select less than 500 words",
          type: "error",
        })
      );
      editor?.commands.unsetHighlight();
      return;
    }

    // Set state for dropdown
    setShowAIDropdown(true);
    setShouldShowBubbleMenu(false);
    setSelectedText(selectedBlocks);

    // Focus and scroll to the element
    setTimeout(() => {
      try {
        // Try direct window scrolling as a backup
        const editorRect = scrollContainerRef.current
          ?.querySelector(".ProseMirror")
          ?.getBoundingClientRect();
        if (editorRect) {
          // Use a simpler approach - just scroll to a fixed offset from the selection
          // This is more reliable across different scroll positions
          const targetScroll = Math.max(0, relativeTop - 150);

          // Find the scrollable container
          let scrollContainer = null;
          let currentEl = editorDOM;

          while (currentEl && currentEl !== document.body) {
            if (currentEl.scrollHeight > currentEl.clientHeight) {
              scrollContainer = currentEl;
              break;
            }
            currentEl = currentEl.parentElement;
          }

          // If we found a scrollable container, scroll it
          if (scrollContainer) {
            scrollContainer.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          } else {
            window.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          }
        }
      } catch (error) {
        console.error("Error during focus/scroll:", error);
      }
    }, 100);
  };

  const handleCloseAIDropdown = () => {
    setShowAIDropdown(false);
    setShouldShowBubbleMenu(true);
    setSelectedText([]);
    setCords({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    });

    // Remove the highlight
    editor?.commands.unsetHighlight();
    setBottomMargin(0);
  };

  const handleSelectFactCheckFinding = useCallback((finding: FactCheckFinding) => {
    if (!editor) return;
    // Applied findings should scroll to the post-apply text; pending to the original.
    const isApplied = (finding.status || (finding.applied ? "applied" : "pending")) === "applied";
    const range = isApplied
      ? findRangeForAppliedFinding(editor, finding, lastPrebuiltRef.current ?? undefined)
      : findRangeForFinding(
          editor,
          finding,
          lastPrebuiltRef.current ?? undefined,
          localOffsetsDirtyRef.current
        );
    if (!range) {
      dispatch(
        snackAdded({
          message: "Could not find this text in the current editor version.",
          type: "warning",
        })
      );
      return;
    }

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .run();

    // Smooth-scroll to the selected text (TipTap's scrollIntoView is a jump)
    requestAnimationFrame(() => {
      try {
        const { node } = editor.view.domAtPos(range.from);
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {}
    });
  }, [dispatch, editor]);

  const handleApplyFactCheckAction = useCallback(
    async (finding: FactCheckFinding) => {
      if (!editor || !id) return;

      // Re-entrancy guard: a second apply (or a decline) firing while this
      // call is in flight can race the response and clobber it. The sidebar
      // disables its buttons via isApplyingFactCheck, but check the ref too
      // in case any other surface invokes the handler.
      if (isApplyingFactCheckRef.current) return;
      isApplyingFactCheckRef.current = true;
      setIsApplyingFactCheck(true);

      try {
        // Drain BOTH save queues before we apply. Either a content save or
        // a fact-check status save (e.g. a decline that just fired) can land
        // *after* /article/apply-finding and clobber the applied markdown
        // with pre-apply content. Both flush helpers await any in-flight HTTP.
        // Inside the try so a flush rejection still releases the lock.
        await Promise.all([flushPendingSaves(), flushPendingFindingsSave()]);
        const currentHtml = editor.getHTML();
        const currentMarkdown = convertToMarkdown(currentHtml);
        const isHumanizedNow = isEditingHumanizedContent.current ?? false;

        const result = await dispatch(
          applyFactCheckFinding({
            briefId: id,
            isHumanized: isHumanizedNow,
            currentMarkdown,
            // Use the ref, not state — flushPendingFindingsSave may have just
            // drained pending status changes that haven't re-rendered yet,
            // so the React state closure could still hold the older list.
            currentFindings: factCheckFindingsRef.current,
            finding,
          })
        ).unwrap();

        // Render the canonical markdown the backend just persisted.
        const html = converter.makeHtml(result.updatedMarkdown || "");

        isProgrammaticChange.current = true;
        try {
          editor.commands.setContent(html);
        } finally {
          isProgrammaticChange.current = false;
        }

        // Backend already saved this content — sync the local refs so the
        // next user edit is correctly recognized as the first unsaved change.
        lastSavedContent.current = html;
        if (isHumanizedNow) {
          humanizedHtmlRef.current = html;
        } else {
          htmlRef.current = html;
        }

        // Replace local findings with the backend's authoritative set.
        setFactCheckFindings(result.updatedFindings as FactCheckFinding[]);

        // The cached resolver segments belong to a stale doc — drop them.
        lastPrebuiltRef.current = null;
        localOffsetsDirtyRef.current = true;

        const action = String(finding.action || "").trim().toLowerCase();
        const successMessage =
          action === "remove"
            ? "Text removed."
            : action === "flag_for_review" && !finding.replacement_text?.trim()
              ? "Marked as reviewed."
              : "Replacement applied.";
        dispatch(snackAdded({ message: successMessage, type: "success" }));
        setActiveFindingId(finding.id);
      } catch (err: any) {
        const code: string | undefined = err?.code;
        const message =
          code === "TEXT_NOT_FOUND"
            ? "Could not locate this text in the article. It may have been edited."
            : code === "AMBIGUOUS_MATCH"
              ? "Multiple matches found — please edit manually to clarify."
              : code === "FINDING_NOT_FOUND"
                ? "This finding is no longer in the current list. Please refresh."
                : err?.message || "Failed to apply finding.";
        dispatch(snackAdded({ message, type: "warning" }));
      } finally {
        isApplyingFactCheckRef.current = false;
        setIsApplyingFactCheck(false);
      }
    },
    [dispatch, editor, id, converter, flushPendingSaves, flushPendingFindingsSave]
  );

  const handleDeclineFactCheckAction = useCallback((finding: FactCheckFinding) => {
    // Don't mutate findings during an in-flight apply — a decline-driven
    // /article/update with pre-apply markdown could land after the apply
    // response and overwrite it. The sidebar already greys out the button,
    // but enforce here too in case any other surface invokes the handler.
    if (isApplyingFactCheckRef.current) return;
    setFactCheckFindings((prev) => {
      const updated = prev.map((item) =>
        item.id === finding.id
          ? { ...item, status: "declined" as const }
          : item
      );
      saveFactCheckFindings(updated, editor?.getHTML() || lastSavedContent.current);
      return updated;
    });
    dispatch(snackAdded({ message: "Suggestion declined.", type: "info" }));
  }, [dispatch, saveFactCheckFindings]);

  const applyFactCheckDecorations = useCallback(() => {
    if (!editor) return;
    const runId = ++decorationRunId.current;

    if (factCheckFindings.length === 0) {
      (editor.commands as any).clearFactCheckRanges?.();
      return;
    }

    // Build text segments once and share across all findings to avoid
    // re-walking the ProseMirror doc for each finding.
    const segmentData = buildTextSegments(editor);
    const prebuilt: PrebuiltSegments = {
      segments: segmentData.segments,
      combinedText: normalizeUnicode(segmentData.combinedText),
    };
    lastPrebuiltRef.current = prebuilt;
    // Skip declined findings — no underline for dismissed suggestions
    const activeFindings = factCheckFindings.filter((f) => f.status !== "declined");
    const results: Array<FactCheckDecorationRange | null> = activeFindings.map((finding) => {
      const effectiveStatus = finding.status || (finding.applied ? "applied" : "pending");
      // Pending findings underline the original_text in place.
      // Applied findings green-highlight the applied_text (preserved post-apply
      // separately from the audit-only original_text). If applied_text is
      // missing or no longer matches the doc, gracefully drop the decoration —
      // the sidebar still shows the finding as Applied.
      const range = effectiveStatus === "applied"
        ? findRangeForAppliedFinding(editor, finding, prebuilt)
        : findRangeForFinding(editor, finding, prebuilt, localOffsetsDirtyRef.current);
      if (!range) return null;
      return {
        ...range,
        isAccepted: effectiveStatus === "applied",
      };
    });

    // Abort if a newer run started while this one was computing
    if (runId !== decorationRunId.current) return;

    const ranges = results.filter(Boolean) as FactCheckDecorationRange[];
    const matched = ranges.length;
    const unmatched = activeFindings.length - matched;
    if (activeFindings.length > 0) {
      console.log(`[FactCheck] Matched ${matched}/${activeFindings.length} active findings (${unmatched} unmatched, ${factCheckFindings.length - activeFindings.length} declined)`);
    }

    (editor.commands as any).setFactCheckRanges?.(ranges);
  }, [editor, factCheckFindings]);

  const unlockArticle = () => {
    setShowPaymentModal(true);

    // Reset flags if using the regular editor
    if (!isOptimizedMode) {
      if (additionalProps.setIsDownloadContent) {
        additionalProps.setIsDownloadContent(false);
      }
      if (additionalProps.setIsCopyContent) {
        additionalProps.setIsCopyContent(false);
      }
    }
  };

  // Effect to handle unlock events from the custom TipTap extension
  useEffect(() => {
    if (!hideArticle || !useScrollBasedTruncation) {
      return;
    }

    const handleUnlockEvent = (event: CustomEvent) => {
      event.preventDefault();
      unlockArticle();
    };

    // Listen for the custom unlock event
    document.addEventListener('unlockArticle', handleUnlockEvent as EventListener);

    return () => {
      document.removeEventListener('unlockArticle', handleUnlockEvent as EventListener);
    };
  }, [hideArticle, useScrollBasedTruncation]);

  // Clean up editor on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Effect for copy/download in regular editor mode
  useEffect(() => {
    if (additionalProps.isCopyContent) {
      handleCopy();
    }
  }, [additionalProps.isCopyContent]);

  useEffect(() => {
    if (additionalProps.isDownloadContent) {
      handleDownload();
    }
  }, [additionalProps.isDownloadContent]);

  // Effect to handle content switching (only on toggle, NOT on save)
  useEffect(() => {
    if (!editor || !isContentLoaded.current) return;

    const switchContent = async () => {
      try {
        // Capture sequence number for this invocation (Fix P1)
        const mySequence = ++toggleSequence.current;

        // Flush CURRENT channel before switching (Fix #3)
        // This ensures any pending save for the current view completes first
        const currentChannel = isEditingHumanizedContent.current ? 'humanized' : 'original';

        // Block toggle on flush error to prevent data loss
        try {
          await flushPendingSaves(currentChannel);
        } catch (error) {
          console.error('[TOGGLE] Save failed during flush, aborting toggle:', error);
          // Revert toggle UI to match the channel we're still editing
          dispatch(setShowHumanizeContent(isEditingHumanizedContent.current));
          dispatch(snackAdded({
            message: "Failed to save changes. Please try toggling again.",
            type: "error",
          }));
          return;
        }

        // Check if a newer toggle was initiated while we were flushing (Fix P1)
        if (mySequence !== toggleSequence.current) {
          return; // Abort silently - newer toggle will handle the switch
        }

        // Read from refs (always latest saved content) instead of state
        const newContent = additionalProps.showHumanizeContent
          ? humanizedHtmlRef.current
          : htmlRef.current;
        const currentContent = editor.getHTML();

        if (newContent !== currentContent) {
          isProgrammaticChange.current = true;

          try {
            // Update tracking ref for new channel
            isEditingHumanizedContent.current = additionalProps.showHumanizeContent;
            setIsEditingHumanizedVersion(additionalProps.showHumanizeContent);

            // Update content
            if (hideArticle && useScrollBasedTruncation) {
              insertUnlockCardWithTipTap(editor, newContent, isOptimizedMode);
            } else {
              editor.commands.setContent(newContent);
            }
          } finally {
            // Clear suppression even if setContent throws (Fix P2)
            // Ensures flag never gets stuck, which would block all future user edits
            isProgrammaticChange.current = false;
          }

          // Update word count after content is rendered
          setTimeout(() => {
            if (editor) {
              setWordCount(getWordCount(editor.getText()));
            }
          }, 100);
        }
      } catch (error) {
        // Catch any unhandled errors in switchContent to prevent unhandled rejection (Fix P2)
        console.error('[TOGGLE] Error during content switch:', error);
      }
    };

    switchContent();
  }, [
    additionalProps.showHumanizeContent,
    editor,
    hideArticle,
    useScrollBasedTruncation,
    isOptimizedMode,
    flushPendingSaves,
  ]);

  useEffect(() => {
    const id = setTimeout(() => applyFactCheckDecorations(), 300);
    return () => clearTimeout(id);
  }, [applyFactCheckDecorations]);

  // Detect a fresh load or fresh fact-check pass by watching the set of
  // finding IDs. When the set changes, the new findings carry offsets
  // computed against current content — clear the dirty flag so Strategy 1
  // is trusted again. Apply/decline updates keep the same IDs and don't
  // reset the flag (which is the whole point of having it).
  useEffect(() => {
    const currentIds = factCheckFindings.map((f) => f.id).sort((a, b) => a - b).join(",");
    if (currentIds !== previousFindingIdsRef.current) {
      localOffsetsDirtyRef.current = false;
      previousFindingIdsRef.current = currentIds;
    }
  }, [factCheckFindings]);

  useEffect(() => {
    return () => {
      if (findingsSaveTimer.current) {
        clearTimeout(findingsSaveTimer.current);
      }
    };
  }, []);

  // Click-to-card: when user clicks on a fact-check underline in the editor,
  // set the active finding so the sidebar card scrolls into view.
  useEffect(() => {
    if (!editor) return;
    const editorEl = editor.view.dom;
    const handleFactCheckClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const decorated = target.closest("[data-fact-check-id]") as HTMLElement | null;
      if (decorated) {
        const id = Number(decorated.getAttribute("data-fact-check-id"));
        if (!isNaN(id) && id) setActiveFindingId(id);
      }
    };
    editorEl.addEventListener("click", handleFactCheckClick);
    return () => editorEl.removeEventListener("click", handleFactCheckClick);
  }, [editor]);

  // Cleanup effect: flush pending saves on unmount (Fix #5 - save-on-unmount)
  useEffect(() => {
    return () => {
      // Flush pending saves on unmount to prevent data loss
      // This is fire-and-forget since unmount cleanup can't be async
      // Catch rejection to prevent unhandled promise rejection (Fix P1)
      flushPendingSaves().catch((error) => {
        console.warn('[UNMOUNT] Failed to flush pending saves:', error);
      });
    };
  }, [flushPendingSaves]);

  // Add a new effect to track isEditingHumanizedVersion changes separately
  useEffect(() => {}, [isEditingHumanizedVersion]);

  // Add this effect to handle humanization
  useEffect(() => {
    const handleHumanization = async () => {
      // Exit early if not humanizing, no editor, or no humanize function
      if (
        !additionalProps.isHumanizing ||
        !editor ||
        !additionalProps.onHumanize
      )
        return;

      try {
        const content = editor.getHTML();

        // Audit stage runs server-side (~2-3s) before the rewrite stream
        // starts. Show the skeleton overlay until the first chunk arrives.
        setIsWaitingForFirstChunk(true);

        // Call the humanize-again streaming Lambda (Function URL, AWS_IAM auth).
        const response = await signedHumanizeFetch({
          text: content,
          source: "ai_writing",
        });

        if (response.status === 429) {
          const data = await response.json();
          dispatch(
            snackAdded({
              message: data.message,
              type: "error",
              duration: 2000,
            })
          );
          dispatch(endHumanization(false));
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder("utf-8");
        let accumulatedText = "";
        let firstChunk = true;

        while (true) {
          const { value, done } = await reader?.read()!;
          if (done) break;

          if (firstChunk) {
            setIsWaitingForFirstChunk(false);
            firstChunk = false;
          }

          const chunk = decoder.decode(value, { stream: true });
          accumulatedText += chunk;

          const html = converter.makeHtml(accumulatedText);
          isProgrammaticChange.current = true;

          // Apply truncation if article should be hidden
          if (hideArticle && useScrollBasedTruncation) {
            insertUnlockCardWithTipTap(editor, html, isOptimizedMode);
          } else {
            editor.commands.setContent(html);
          }

          isProgrammaticChange.current = false;
        }
        // Save content and update state
        await handleSaveContent(editor.getHTML(), {
          isHumanized: true,
          isEdited: false,
        });

        // Update states in correct order
        dispatch(endHumanization(true));

        // Fetch latest content
        await loadContent();

        // Content will be updated through the normal fetch mechanism
      } catch (error) {
        console.error("Error humanizing content:", error);
        dispatch(
          snackAdded({
            message: "Failed to humanize content",
            type: "error",
            duration: 2000,
          })
        );
      } finally {
        setIsWaitingForFirstChunk(false);
      }
    };

    handleHumanization();
  }, [additionalProps.isHumanizing, editor, additionalProps.onHumanize]);

  // Add this effect to handle navigation blocking during humanization
  useEffect(() => {
    // Block navigation while humanizing
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (additionalProps.isHumanizing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    // Block Next.js client-side navigation
    const handleRouteChange = (url: string) => {
      if (additionalProps.isHumanizing) {
        // Prevent the default behavior
        window.history.pushState(null, "", window.location.href);
        // Show warning
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
        throw "Navigation cancelled";
      }
    };

    // Handle popstate (browser back/forward buttons)
    const handlePopState = () => {
      if (additionalProps.isHumanizing) {
        // Push current URL back to history to prevent navigation
        window.history.pushState(null, "", window.location.href);
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    router.events.on("routeChangeStart", handleRouteChange);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [additionalProps.isHumanizing]);

  // Add effect to log when showHumanizeContent changes
  useEffect(() => {}, [additionalProps.showHumanizeContent]);

  const handleToggle = (event) => {
    const isChecked = event.target.checked;
    // Just update the display preference
    dispatch(setShowHumanizeContent(isChecked));
  };

  const checArticleLimit = async () => {
    // Refresh the current plan
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    const numberOfAIWords = Number(currentPlan?.numberOfWordsInPlan || 0);
    const numberOfArticles = Number(currentPlan?.numberOfArticlesInPlan || 0);
    let aiWordsLeft = numberOfAIWords - (currentPlan?.totalWordsWritten || 0);
    let articlesLeft =
      numberOfArticles - (currentPlan?.totalArticlesWritten || 0);
    if (aiWordsLeft < 0) {
      aiWordsLeft = 0;
    }
    if (articlesLeft < 0) {
      articlesLeft = 0;
    }

    if (aiWordsLeft === 0 || articlesLeft === 0) {
      let text = "";
      if (articlesLeft === 0) {
        text = `You've reached a limit for Updating Articles`;
      } else {
        text = ` You've reached a limit of word count`;
      }

      dispatch(aiWordsUpgradeModalShowed(text));
      return true;
    }
    return false;
  };
  const handleUpdateArticleEEAT = async () => {
    setIsOptimizeContentDialogLoading(true);
    setIsOptimizeContentDialogOpen(false);
    const planDetails = (await dispatch(getActivePlan())) as any;
    let plan = planDetails?.payload?.plan ?? undefined;

    if (
      plan === undefined ||
      Object.keys(plan).length === 0 ||
      planDetails?.payload?.error
    ) {
      setShowOnboardingModal(true);
      setIsOptimizeContentDialogLoading(false);
      return;
    }

    const isArticleLimitReached = await checArticleLimit();

    setIsOptimizeContentDialogLoading(false);
    if (!isArticleLimitReached) {
      setIsOptimizeContentDialogOpen(true);
    }
  };
  const handleCloseOptimizeContentDialog = () => {
    setIsOptimizeContentDialogOpen(false);
  };
  const handleOptimizeContentDialogOpen = () => {
    setIsOptimizeContentDialogOpen(true);
  };
  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);

          // Reset flags if using the regular editor
          if (!isOptimizedMode) {
            if (additionalProps.setIsDownloadContent) {
              additionalProps.setIsDownloadContent(false);
            }
            if (additionalProps.setIsCopyContent) {
              additionalProps.setIsCopyContent(false);
            }
          }
        }}
        location={""}
      />
      <ImageGenerationModal
        open={showImageGenModal}
        onClose={() => setShowImageGenModal(false)}
        loading={imageGenLoading}
        error={imageGenError}
        onRetry={handleGenerateImages}
      />
      {showOnboardingModal && (
        <Onboarding
          onClose={() => setShowOnboardingModal(false)}
          isEEATMode={isEEATMode}
          articleLink={articleLink}
        />
      )}
      <OptimizeContentDialog
        open={isOptimizeContentDialogOpen}
        limit={false}
        onClose={handleCloseOptimizeContentDialog}
        onCreated={handleOptimizeContentDialogOpen}
        isRedirect={false}
        isShowInputs={false}
        link={articleLink}
      />
      <Box
        sx={{
          width: "100%",
          margin: "auto",
          position: "relative",
          display: "flex",
          gap: 3,
          px: 3,
          height: "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "100%",
            overflow: "auto",
            position: "relative",
          }}
        >
          <Box
            data-testid="article-editor-toolbar-shell"
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: hideArticle || isEEATMode ? 1 : 20,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: isWritingMode ? "1180px" : "1000px",
                backgroundColor: "#fff",
                borderBottom: "1px solid #EDEFF5",
                boxShadow: "0 2px 10px rgba(18, 24, 40, 0.05)",
              }}
            >
              <MenuBar
                editor={editor}
                handleAidropdown={handleAIDropdown}
                evaluationData={evaluationData}
                isEEATMode={isEEATMode}
                isWritingMode={isWritingMode}
                onToggleWritingMode={() =>
                  setIsWritingMode((prevMode) => !prevMode)
                }
                onGenerateImages={isImageGenEnabled ? handleGenerateImages : undefined}
              />
            </Box>
          </Box>
          <Card
            sx={{
              width: "100%",
              maxWidth: isWritingMode ? "1180px" : "1000px",
              mt: "12px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              flex: 1,
              ...(additionalProps.isHumanizing && {
                minHeight: "calc(100vh - 64px)",
              }),
            }}
          >
            <Editor
              editor={editor}
              isHumanizer={false}
              isLoading={isLoading}
              title={title}
              description={description}
              wordCount={wordCount}
            />

            {/* Audit-stage loader: covers the editor between Humanize click
                and first streamed chunk (~2-3s while the audit runs).
                Three motion cues so the user sees we're actively working:
                  - LinearProgress bar at the top (sliding)
                  - CircularProgress next to the caption (spinning)
                  - Skeleton lines below (subtle wave shimmer)
                Always mounted; toggled via display so React never has to
                unmount it while Tiptap is mutating the editor subtree
                (which caused a NotFoundError insertBefore crash). */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "background.paper",
                zIndex: 2,
                display: isWaitingForFirstChunk ? "flex" : "none",
                flexDirection: "column",
              }}
            >
              <LinearProgress />
              <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <CircularProgress size={16} thickness={5} />
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.75, fontStyle: "italic" }}
                  >
                    Analyzing article…
                  </Typography>
                </Box>
                <Skeleton variant="text" animation="wave" width="40%" sx={{ fontSize: "1.5rem" }} />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="92%" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="88%" />
                <Skeleton variant="text" animation="wave" width="60%" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="78%" />
              </Box>
            </Box>

            {/* Render skeletons below the Editor for hiding content - only when not using scroll-based truncation */}
            {hideArticle && !useScrollBasedTruncation && (
              <Box
                sx={{
                  width: "100%",
                  padding: "16px",
                  background: "linear-gradient(to bottom, transparent, white)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Skeleton Loaders */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={15}
                    width={"65%"}
                    animation={false}
                  />
                </Box>

                {/* Unlock Card */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "65px",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#5048E5",
                      borderRadius: "10px",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        height: "100%",
                        width: "100%",
                        justifyContent: "space-between",
                        padding: "10px 16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Your {isOptimizedMode ? "optimized " : ""}content is
                          ready
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Subscribe to see the full article
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={unlockArticle}
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                            padding: "8px 16px",
                            background: "#EB1F8D !important",
                            width: "150px",
                            height: "49px",
                            borderRadius: "8px",
                          }}
                        >
                          Unlock Now
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}

            <BubbleMenu
              editor={editor}
              className="bubble-menu"
              tippyOptions={{
                duration: 100,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "120px",
                  height: "40px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
                onClick={handleAIDropdown}
              >
                <MagicWandIcon sx={{ mt: 1 }} />
                Ask AI
              </Button>
            </BubbleMenu>

            {showAIDropdown && (
              <RegenerateDropdown
                startCord={cords.top}
                endCord={cords.left}
                bottomCord={cords.bottom}
                rightCord={cords.right}
                open={showAIDropdown}
                selectedText={selectedText}
                editor={editor}
                onClose={handleCloseAIDropdown}
                setMarginBottom={(margin) => setBottomMargin(margin)}
                scrollContainerRef={scrollContainerRef}
              />
            )}
          </Card>
        </Box>
        {/* Sidebar */}

        <Box
          sx={{
            width: "384px",
            height: "10%",
            display: isWritingMode ? "none" : "block",
            position: "relative",
            zIndex: 3,
          }}
        >
          <Sidebar
            title={
              isOptimizedMode ? "EEAT Score Comparison" : "Article EEAT Score"
            }
            recommendedScore={8.0}
            evaluationData={evaluationData}
            optimizedMode={isOptimizedMode}
            isLoading={isLoading}
            isEEATMode={isEEATMode}
            onUpdateArticleEEAT={handleUpdateArticleEEAT}
            isEEATLoading={isOptimizeContentDialogLoading}
            factCheckFindings={factCheckFindings}
            factCheckAutoReview={factCheckAutoReview}
            onSelectFactCheckFinding={handleSelectFactCheckFinding}

            onApplyFactCheckAction={handleApplyFactCheckAction}
            onDeclineFactCheckAction={handleDeclineFactCheckAction}
            activeFindingId={activeFindingId}
            isApplyingFactCheck={isApplyingFactCheck}
          />
        </Box>
      </Box>
    </>
  );
};

export default ArticleEditorComponent;
